import { EnabledProctoringOptions } from "@verity/api-client/types";
import {
	getProctoringOptionDisabledDetails,
	getUnsupportingPlatforms,
	isAnyOnValueEnabledForProctoringOption,
	isOptionSupportedForPlatform,
	isPlatformSelected,
	isProctoringOptionOn,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import { OptionRelatedInstituteSettings, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { useCallback, useMemo } from "react";
import i18nPackage from "../../../i18n/i18n";
import { VerityInstituteSettings } from "../../../types/common";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import {
	getOptionsInCategory,
	PROCTORING_OPTION_CATEGORIES,
	PROCTORING_OPTION_TEXT,
} from "../option-text/ProctoringOptionText";
/**
 * Hook that wraps the proctoring option methods. So we don't keep passing the current state everytime
 */

const CANDIDATE_ONLY_OPTIONS = ["enableCodeEditor"];
export const useProctoringHelpers = (overrides?: {
	verityInstituteSettings?: VerityInstituteSettings;
	enabledProctoringOptions?: EnabledProctoringOptions;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	proctoringOption?: Partial<ProctoringOption>;
}) => {
	const context = useProctoringOptionContext();
	const verityInstituteSettings = overrides?.verityInstituteSettings ?? context.verityInstituteSettings;
	const enabledProctoringOptions = overrides?.enabledProctoringOptions ?? context.enabledProctoringOptions;
	const optionRelatedInstituteSettings =
		overrides?.optionRelatedInstituteSettings ?? context.optionRelatedInstituteSettings;
	const proctoringOption = overrides?.proctoringOption ?? context.proctoringOption;

	const { quizType } = context;

	/**
	 * Given a proctoring option, returns if we should even show the option in the UI
	 */
	const shouldShowOption = useCallback(
		(option: keyof ProctoringOption) => {
			const optionInfo = PROCTORING_OPTION_INFO[option];
			const onValues = optionInfo?.isList ? [[""]] : (optionInfo?.onValues ?? []);

			const application = localStorage.getItem("app");

			if (application !== "candidate" && CANDIDATE_ONLY_OPTIONS.includes(option)) return false;

			if (option === "uploadScratchNotes" && quizType === "verityAssignment") return false;

			return onValues.some((onValue) => {
				const disabledDetails = getProctoringOptionDisabledDetails(
					option,
					onValue,
					proctoringOption,
					verityInstituteSettings,
					optionRelatedInstituteSettings,
					enabledProctoringOptions,
				);
				return !Object.keys(disabledDetails.disabledReasons).some(
					(reason) => reason === "platformUnsupported" || reason === "liteMode" || reason === "platformNotAllowed",
				);
			});
		},
		[verityInstituteSettings, enabledProctoringOptions, optionRelatedInstituteSettings, proctoringOption, quizType],
	);

	/**
	 * Given a proctoring option, returns if the proctoring option is allowed based on current settings
	 */
	const isOptionAllowed = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) => {
			return value === undefined
				? isAnyOnValueEnabledForProctoringOption(
						option,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					)
				: !getProctoringOptionDisabledDetails(
						option,
						value,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					).disabled;
		},
		[verityInstituteSettings, enabledProctoringOptions, optionRelatedInstituteSettings, proctoringOption],
	);

	/**
	 *  Given an option, returns the list of selected platforms that are supported for that option.
	 *  If the option is supported for all selected platforms, returns an empty set.
	 */
	const getPartiallySupportedPlatforms = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]): Set<keyof ProctoringPlatforms> => {
			const optionInfo = PROCTORING_OPTION_INFO[option];

			const valuesToCheck =
				value !== undefined
					? [value]
					: optionInfo?.isList
						? [[""] as ProctoringOption[O]]
						: (optionInfo?.onValues ?? []);

			const supportedPlatforms = new Set<keyof ProctoringPlatforms>();
			if (!proctoringOption) return new Set();
			const selectedPlatforms = (
				["isExtension", "isLockdown", "isMobile", "isChromeOs"] as (keyof ProctoringPlatforms)[]
			).filter((platform) => isPlatformSelected(proctoringOption, platform));

			selectedPlatforms.forEach((platform) => {
				const isSupported = valuesToCheck.some((v) => isOptionSupportedForPlatform(platform, option, v));
				if (isSupported) {
					supportedPlatforms.add(platform);
				}
			});

			// If all selected platforms are supported, return empty
			if (supportedPlatforms.size === selectedPlatforms.length) return new Set();
			return new Set(supportedPlatforms);
		},
		[proctoringOption],
	);

	const allowedCategories = useMemo(() => {
		return PROCTORING_OPTION_CATEGORIES.filter((category) => getOptionsInCategory(category).some(shouldShowOption));
	}, [shouldShowOption]);

	const getDisabledMessage = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) => {
			const optionInfo = PROCTORING_OPTION_INFO[option];
			const onValues = optionInfo?.isList ? [[""] as ProctoringOption[O]] : (optionInfo?.onValues ?? []);

			// If a specific value was passed, check only that. Otherwise, check all 'on' values.
			const valuesToCheck = value !== undefined ? [value] : onValues;

			// Map them to their disabled details
			const results = valuesToCheck.map((v) =>
				getProctoringOptionDisabledDetails(
					option,
					v,
					proctoringOption,
					verityInstituteSettings,
					optionRelatedInstituteSettings,
					enabledProctoringOptions,
				),
			);

			// If even ONE 'on' value is allowed, there is no "Disabled Message" needed.
			if (results.some((r) => !r.disabled)) {
				return undefined;
			}

			const firstDisabledResult = results[0];
			const reasons = Object.keys(firstDisabledResult.disabledReasons);
			const valueToCheck = valuesToCheck[0];

			if (reasons.includes("instituteDisabledOption")) {
				return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disabledReason.instituteDisabled");
			}

			// Pretty sure this will never show because we don't show the proctoring option if the platform doesn't support it
			if (reasons.includes("platformNotAllowed")) {
				const unsupportingPlatforms = getUnsupportingPlatforms(proctoringOption, option, valueToCheck);
				const platformNames = unsupportingPlatforms.map((x) => PROCTORING_OPTION_TEXT[x].header);
				const platformsString = new Intl.ListFormat(i18nPackage.language, {
					style: "long",
					type: "conjunction",
				}).format(platformNames);
				const isToggled = isProctoringOptionOn(option, valueToCheck);
				const optionHeader = PROCTORING_OPTION_TEXT[option].header;

				const translationKey = isToggled ? "platformDisable" : "platformEnable";
				return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.disabledReason.${translationKey}`, {
					platforms: platformsString,
					option: optionHeader,
					count: unsupportingPlatforms.length,
				});
			}
			// can add other disabled reason texts

			return undefined;
		},
		[enabledProctoringOptions, optionRelatedInstituteSettings, proctoringOption, verityInstituteSettings],
	);

	return {
		isOptionAllowed,
		getPartiallySupportedPlatforms,
		shouldShowOption,
		allowedCategories,
		getDisabledMessage,
	};
};
